import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  mergeClasses,
  MessageBar,
  Option,
  SelectTabData,
  SelectTabEvent,
  Spinner,
  Tab,
  TabList,
  Text,
  Textarea,
} from "@fluentui/react-components";
import {
  AddRegular,
  ArrowLeftRegular,
  ArrowRightRegular,
  CheckmarkCircleRegular,
  CheckmarkRegular,
  DeleteRegular,
  DismissRegular,
  EditRegular,
  SparkleRegular,
} from "@fluentui/react-icons";
import {
  getProjectIqWorkspaces,
  getSowAnalysis,
  saveProjectPlan,
} from "apps/project-iq/api";
import {
  MILESTONE_STATUS_LABELS,
  RAID_STATUS_LABELS,
} from "apps/project-iq/constants/planStatusLabels";
import { useLinks } from "apps/project-iq/hooks/useLinks";
import { DeliverablesPage } from "apps/project-iq/pages/DeliverablesPage/DeliverablesPage";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import { IAdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { DialogTabs } from "components/dialogTabs/DialogTabs";
import { PeoplePicker } from "components/fields/peoplePicker/PeoplePicker";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { IAzureUser } from "models/IAzureUser";
import { IProjectIQWorkspace } from "models/IProjectIQWorkspace";
import { ITableColumn } from "models/ITableColumn";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAvailableProjectIqWorkspaces,
  updateProjectIqWorkspace as updateWorkspaceAction,
} from "redux/projectIqWorkspacesSlice";
import { RootState } from "store";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { formatDate, mapAzureUsersToModel } from "utils/generalUtils";
import { useWorkspacePlanStyles } from "./WorkspacePlan.styles";
import {
  IMilestone,
  IRagDimension,
  IRaidEntry,
  ISowAnalysis,
  IWorkspacePlan,
  IWorkspacePlanProps,
  MilestoneStatus,
  PlanSaveStatus,
  PlanTab,
  RagStatus,
  RaidCategory,
  RaidSeverity,
  RaidStatus,
} from "./WorkspacePlan.types";

const INITIAL_RAG_DIMENSIONS: IRagDimension[] = [
  { id: "overall", label: "Overall", status: "green", statusText: "On track" },
  { id: "budget", label: "Budget", status: "green", statusText: "On track" },
  {
    id: "schedule",
    label: "Schedule",
    status: "green",
    statusText: "On track",
  },
  { id: "scope", label: "Scope", status: "green", statusText: "On track" },
];

const INITIAL_MILESTONES: IMilestone[] = [];

const INITIAL_RAID_ENTRIES: IRaidEntry[] = [];

const RAG_CYCLE: Record<RagStatus, RagStatus> = {
  green: "amber",
  amber: "red",
  red: "green",
};

const MIN_PLAN_DATE = "1900-01-01";
const MAX_PLAN_DATE = "2100-12-31";
const INVALID_DATE_MESSAGE = "Invalid date";

const validatePlanDate = (value?: string): string => {
  if (!value) return "";
  if (Number.isNaN(new Date(value + "T00:00:00").getTime()))
    return INVALID_DATE_MESSAGE;
  if (value < MIN_PLAN_DATE || value > MAX_PLAN_DATE)
    return INVALID_DATE_MESSAGE;
  return "";
};

const ragDateErrorKey = (
  dimensionId: string,
  field: "startDate" | "endDate",
): string => `${dimensionId}-${field}`;

const computeWeekLabel = (startDate?: string, endDate?: string): string => {
  if (!startDate || !endDate) return "—";
  if (validatePlanDate(startDate) || validatePlanDate(endDate)) return "—";
  const start = new Date(startDate + "T00:00:00");
  const end = new Date(endDate + "T00:00:00");
  const now = new Date();
  if (now < start) return "Not started";
  if (now > end) return "Complete";
  const totalWeeks = Math.ceil(
    (end.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000),
  );
  const currentWeek = Math.ceil(
    (now.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000),
  );
  return `Week ${currentWeek} of ${totalWeeks}`;
};

const RAG_DEFAULT_TEXT: Record<RagStatus, string> = {
  green: "On track",
  amber: "At risk",
  red: "Off track",
};

const MILESTONE_STATUS_OPTIONS: { value: MilestoneStatus; label: string }[] = [
  { value: "not_started", label: MILESTONE_STATUS_LABELS.not_started },
  { value: "in_progress", label: MILESTONE_STATUS_LABELS.in_progress },
  { value: "done", label: MILESTONE_STATUS_LABELS.done },
];

const RAID_CATEGORY_OPTIONS: { value: RaidCategory; label: string }[] = [
  { value: "risk", label: "Risk" },
  { value: "action", label: "Action" },
  { value: "issue", label: "Issue" },
  { value: "decision", label: "Decision" },
];

const RAID_SEVERITY_OPTIONS: { value: RaidSeverity; label: string }[] = [
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const RAID_STATUS_OPTIONS: { value: RaidStatus; label: string }[] = [
  { value: "not_started", label: RAID_STATUS_LABELS.not_started },
  { value: "in_progress", label: RAID_STATUS_LABELS.in_progress },
  { value: "closed", label: RAID_STATUS_LABELS.closed },
];

const SOW_ANALYSIS_POLL_INTERVAL_MS = 2000;
// Stop polling after ~5 minutes so a task that dies without ever leaving
// "processing" (killed worker, broker issue) doesn't poll forever with a
// non-dismissible spinner banner stuck on screen.
const SOW_ANALYSIS_MAX_POLL_ATTEMPTS = 150;

// The actions column only needs to be wide enough for Edit+Delete unless a
// SOW badge/confirm icon can also appear on at least one row.
const ACTIONS_COLUMN_WIDTH_NARROW = "96px";
const ACTIONS_COLUMN_WIDTH_WIDE = "150px";

export const WorkspacePlan = ({
  workspaceId,
  onSaveStatusChange,
  initialTab = "milestones" as PlanTab,
}: IWorkspacePlanProps): JSX.Element => {
  const styles = useWorkspacePlanStyles();
  const { links: deliverableLinks } = useLinks("engagement_plan_deliverables");
  const colStyles = useAdminColumnStyles();
  const contentStyles = useSharedDialogContentStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const dispatch = useDispatch();
  const workspaces = useSelector(selectAvailableProjectIqWorkspaces);
  const workspace = workspaces.find((w) => w.id === workspaceId);
  const users = useSelector((state: RootState) => state.usersSlice.value);
  const mappedUsers = useMemo(() => mapAzureUsersToModel(users), [users]);

  const readResourcesForPickerFieldFiltered = useCallback(
    async (searchedValue: string): Promise<IAzureUser[]> =>
      mappedUsers.filter((user: IAzureUser) =>
        user.Name?.toLowerCase().includes(searchedValue.toLowerCase()),
      ),
    [mappedUsers],
  );

  const [ragDimensions, setRagDimensions] = useState<IRagDimension[]>(
    INITIAL_RAG_DIMENSIONS,
  );
  const [milestones, setMilestones] =
    useState<IMilestone[]>(INITIAL_MILESTONES);
  const [raidEntries, setRaidEntries] =
    useState<IRaidEntry[]>(INITIAL_RAID_ENTRIES);
  const [saveStatus, setSaveStatus] = useState<PlanSaveStatus>("idle");
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isRaidDialogOpen, setIsRaidDialogOpen] = useState<boolean>(false);
  const [activeRaidTab, setActiveRaidTab] = useState<"overview" | "action">(
    "overview",
  );
  const [dialogDraft, setDialogDraft] = useState<IRaidEntry | null>(null);
  const [dialogOwnerOptions, setDialogOwnerOptions] = useState<CustomOption[]>(
    [],
  );
  const [dialogNameError, setDialogNameError] = useState<string>("");
  const [dialogDueDateError, setDialogDueDateError] = useState<string>("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteMilestoneId, setConfirmDeleteMilestoneId] = useState<
    string | null
  >(null);
  const [isMilestoneDialogOpen, setIsMilestoneDialogOpen] =
    useState<boolean>(false);
  const [milestoneDraft, setMilestoneDraft] = useState<IMilestone | null>(null);
  const [milestoneNameError, setMilestoneNameError] = useState<string>("");
  const [milestoneDueDateError, setMilestoneDueDateError] =
    useState<string>("");
  const [ragDateErrors, setRagDateErrors] = useState<Record<string, string>>(
    {},
  );
  const [editingRagStatusId, setEditingRagStatusId] = useState<string | null>(
    null,
  );
  const [activePlanTab, setActivePlanTab] = useState<PlanTab>(initialTab);
  const [sowAnalysisStatus, setSowAnalysisStatus] =
    useState<ISowAnalysis | null>(null);
  const [isSowBannerDismissed, setIsSowBannerDismissed] = useState(false);
  const [isSowAnalysisPollTimedOut, setIsSowAnalysisPollTimedOut] =
    useState(false);
  const sowAnalysisPollRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const sowAnalysisPollAttemptsRef = useRef(0);

  useEffect(() => {
    setActivePlanTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    onSaveStatusChange?.(saveStatus);
  }, [saveStatus, onSaveStatusChange]);

  useEffect(() => {
    const plan = workspace?.projectPlan as IWorkspacePlan | undefined;
    if (!plan) return;
    if (plan.ragDimensions) {
      setRagDimensions((prev) =>
        prev === INITIAL_RAG_DIMENSIONS ? plan.ragDimensions! : prev,
      );
    }
    if (plan.milestones) {
      setMilestones((prev) => (prev.length === 0 ? plan.milestones! : prev));
    }
    if (plan.raidEntries) {
      setRaidEntries((prev) => (prev.length === 0 ? plan.raidEntries! : prev));
    }
  }, [workspace?.id, workspace?.sowAnalysis?.completedAt]);

  const save = async (plan: IWorkspacePlan) => {
    if (!workspace) return;

    setSaveStatus("saving");
    try {
      const updated = await saveProjectPlan({
        id: workspaceId,
        projectPlan: plan,
      });
      dispatch(
        updateWorkspaceAction({
          ...workspace,
          projectPlan: updated.projectPlan,
        }),
      );
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch {
      setSaveStatus("error");
    }
  };

  const scheduleSave = (plan: IWorkspacePlan) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => save(plan), 800);
  };

  const stopSowAnalysisPolling = useCallback(() => {
    if (sowAnalysisPollRef.current) {
      clearInterval(sowAnalysisPollRef.current);
      sowAnalysisPollRef.current = null;
    }
  }, []);

  const refreshWorkspace = useCallback(async (): Promise<void> => {
    try {
      const workspaces: IProjectIQWorkspace[] = await getProjectIqWorkspaces();
      const updatedWorkspace = workspaces.find(
        (w: IProjectIQWorkspace) => w.id === workspaceId,
      );
      if (updatedWorkspace) {
        dispatch(updateWorkspaceAction(updatedWorkspace));
      }
    } catch {
      // Silently continue on error
    }
  }, [workspaceId, dispatch]);

  useEffect(() => {
    void refreshWorkspace();
  }, [refreshWorkspace]);

  const startSowAnalysisPolling = useCallback(() => {
    stopSowAnalysisPolling();
    sowAnalysisPollAttemptsRef.current = 0;
    setIsSowAnalysisPollTimedOut(false);
    sowAnalysisPollRef.current = setInterval(async () => {
      sowAnalysisPollAttemptsRef.current += 1;
      if (sowAnalysisPollAttemptsRef.current > SOW_ANALYSIS_MAX_POLL_ATTEMPTS) {
        stopSowAnalysisPolling();
        setIsSowAnalysisPollTimedOut(true);
        return;
      }
      try {
        const status = await getSowAnalysis({ workspaceId });
        setSowAnalysisStatus(status);

        if (
          status.status === "completed" ||
          status.status === "failed" ||
          status.status === "skipped_not_empty" ||
          status.status === "skipped_no_content"
        ) {
          stopSowAnalysisPolling();
          await refreshWorkspace();
        }
      } catch {
        // Silently continue polling on error
      }
    }, SOW_ANALYSIS_POLL_INTERVAL_MS);
  }, [workspaceId, stopSowAnalysisPolling, refreshWorkspace]);

  useEffect(() => {
    return () => stopSowAnalysisPolling();
  }, [stopSowAnalysisPolling]);

  useEffect(() => {
    const status = workspace?.sowAnalysis?.status;
    if (status === "processing") {
      setSowAnalysisStatus(workspace?.sowAnalysis ?? null);
      setIsSowBannerDismissed(false);
      startSowAnalysisPolling();
    } else {
      setSowAnalysisStatus(workspace?.sowAnalysis || null);
      stopSowAnalysisPolling();
    }
  }, [
    workspace?.sowAnalysis?.status,
    workspace?.sowAnalysis,
    startSowAnalysisPolling,
    stopSowAnalysisPolling,
  ]);

  const handleRagCycle = (id: string) => {
    const next = ragDimensions.map((d) => {
      if (d.id !== id) return d;
      const nextStatus = RAG_CYCLE[d.status];
      const isDefaultText = d.statusText === RAG_DEFAULT_TEXT[d.status];
      return {
        ...d,
        status: nextStatus,
        statusText: isDefaultText ? RAG_DEFAULT_TEXT[nextStatus] : d.statusText,
      };
    });
    setRagDimensions(next);
    void save({ ragDimensions: next, milestones, raidEntries });
  };

  const handleRagStatusTextChange = (id: string, text: string) => {
    const next = ragDimensions.map((d) =>
      d.id === id ? { ...d, statusText: text } : d,
    );
    setRagDimensions(next);
    scheduleSave({ ragDimensions: next, milestones, raidEntries });
  };

  const handleRagDateChange = (
    id: string,
    field: "startDate" | "endDate",
    value: string,
  ) => {
    const dateError = validatePlanDate(value);
    setRagDateErrors((prev) => ({
      ...prev,
      [ragDateErrorKey(id, field)]: dateError,
    }));
    const next = ragDimensions.map((d) =>
      d.id === id ? { ...d, [field]: value } : d,
    );
    setRagDimensions(next);
    if (dateError) return;

    scheduleSave({ ragDimensions: next, milestones, raidEntries });
  };

  const handleOpenMilestoneDialog = (milestone?: IMilestone) => {
    setMilestoneDraft(
      milestone
        ? { ...milestone }
        : {
            id: crypto.randomUUID(),
            name: "",
            description: "",
            dueDate: "",
            status: "not_started",
          },
    );
    setMilestoneNameError("");
    setMilestoneDueDateError("");
    setIsMilestoneDialogOpen(true);
  };

  const handleCloseMilestoneDialog = () => {
    setIsMilestoneDialogOpen(false);
    setMilestoneDraft(null);
    setMilestoneNameError("");
    setMilestoneDueDateError("");
  };

  const handleDraftMilestoneChange = (
    field: keyof IMilestone,
    value: string,
  ) => {
    setMilestoneDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const validateMilestoneName = (name: string): string => {
    if (!name.trim()) return "Name is required";
    const isDuplicate = milestones.some(
      (m) =>
        m.id !== milestoneDraft?.id &&
        m.name.trim().toLowerCase() === name.trim().toLowerCase(),
    );
    if (isDuplicate) return "A milestone with this name already exists";
    return "";
  };

  const handleSaveMilestone = () => {
    if (!milestoneDraft) return;

    const nameError = validateMilestoneName(milestoneDraft.name);
    const dueDateError = validatePlanDate(milestoneDraft.dueDate);
    setMilestoneNameError(nameError);
    setMilestoneDueDateError(dueDateError);
    if (nameError || dueDateError) return;

    const savedMilestone: IMilestone = {
      ...milestoneDraft,
      isDraftedFromSow: false,
    };
    const next = milestones.some((m) => m.id === savedMilestone.id)
      ? milestones.map((m) => (m.id === savedMilestone.id ? savedMilestone : m))
      : [...milestones, savedMilestone];
    setMilestones(next);
    void save({ ragDimensions, milestones: next, raidEntries });
    handleCloseMilestoneDialog();
  };

  const handleDeleteMilestone = (id: string) => {
    setConfirmDeleteMilestoneId(id);
  };

  const handleConfirmDeleteMilestone = () => {
    if (!confirmDeleteMilestoneId) return;

    const next = milestones.filter((m) => m.id !== confirmDeleteMilestoneId);
    setMilestones(next);
    setConfirmDeleteMilestoneId(null);
    void save({ ragDimensions, milestones: next, raidEntries });
  };

  const handleCancelDeleteMilestone = () => {
    setConfirmDeleteMilestoneId(null);
  };

  const handleConfirmKeepMilestone = (milestone: IMilestone) => {
    const next = milestones.map((m) =>
      m.id === milestone.id ? { ...m, isDraftedFromSow: false } : m,
    );
    setMilestones(next);
    void save({ ragDimensions, milestones: next, raidEntries });
  };

  const handleDeleteRaidEntry = (id: string) => {
    setConfirmDeleteId(id);
  };

  const handleConfirmDelete = () => {
    if (!confirmDeleteId) return;

    const next = raidEntries.filter((e) => e.id !== confirmDeleteId);
    setRaidEntries(next);
    setConfirmDeleteId(null);
    void save({ ragDimensions, milestones, raidEntries: next });
  };

  const handleCancelDelete = () => {
    setConfirmDeleteId(null);
  };

  const handleConfirmKeepRaidEntry = (entry: IRaidEntry) => {
    const next = raidEntries.map((e) =>
      e.id === entry.id ? { ...e, isDraftedFromSow: false } : e,
    );
    setRaidEntries(next);
    void save({ ragDimensions, milestones, raidEntries: next });
  };

  const handleOpenRaidDialog = (entry?: IRaidEntry) => {
    setDialogDraft(
      entry
        ? { ...entry }
        : {
            id: crypto.randomUUID(),
            name: "",
            category: "risk",
            severity: "medium",
            description: "",
            responseMitigation: "",
            dueDate: "",
            owner: "",
            status: "not_started",
          },
    );
    setDialogOwnerOptions(
      entry?.owner ? [{ key: entry.owner, text: entry.owner }] : [],
    );
    setDialogNameError("");
    setDialogDueDateError("");
    setActiveRaidTab("overview");
    setIsRaidDialogOpen(true);
  };

  const handleSaveRaidEntry = () => {
    if (!dialogDraft) return;

    const nameError = validateRaidName(dialogDraft.name);
    const dueDateError = validatePlanDate(dialogDraft.dueDate);
    setDialogNameError(nameError);
    setDialogDueDateError(dueDateError);
    if (nameError || dueDateError) {
      setActiveRaidTab(nameError ? "overview" : "action");
      return;
    }
    const entry = {
      ...dialogDraft,
      owner: dialogOwnerOptions[0]?.text ?? "",
      isDraftedFromSow: false,
    };
    const next = raidEntries.some((e) => e.id === entry.id)
      ? raidEntries.map((e) => (e.id === entry.id ? entry : e))
      : [...raidEntries, entry];
    setRaidEntries(next);
    void save({ ragDimensions, milestones, raidEntries: next });
    setIsRaidDialogOpen(false);
    setDialogDraft(null);
    setDialogOwnerOptions([]);
  };

  const handleDialogClose = () => {
    setIsRaidDialogOpen(false);
    setDialogDraft(null);
    setDialogOwnerOptions([]);
    setDialogNameError("");
    setDialogDueDateError("");
    setActiveRaidTab("overview");
  };

  const handleDraftChange = (field: keyof IRaidEntry, value: string) => {
    setDialogDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const validateRaidName = (name: string): string => {
    if (!name.trim()) return "Name is required";
    const isDuplicate = raidEntries.some(
      (entry) =>
        entry.id !== dialogDraft?.id &&
        (entry.name?.trim() ?? "").toLowerCase() === name.trim().toLowerCase(),
    );
    if (isDuplicate) return "An entry with this name already exists";

    return "";
  };

  const ragDotClass = (status: RagStatus) =>
    status === "green"
      ? styles.ragDotGreen
      : status === "amber"
        ? styles.ragDotAmber
        : styles.ragDotRed;

  const raidSeverityClass = (severity: RaidSeverity) =>
    severity === "high"
      ? styles.raidSeverityHigh
      : severity === "medium"
        ? styles.raidSeverityMedium
        : styles.raidSeverityLow;

  const statusTriggerClass = (status: MilestoneStatus) =>
    mergeClasses(
      styles.statusTrigger,
      status === "done"
        ? styles.statusTriggerComplete
        : status === "in_progress"
          ? styles.statusTriggerInProgress
          : styles.statusTriggerUpcoming,
    );

  const {
    searchQuery: milestoneSearchQuery,
    handleSearchChange: handleMilestoneSearchChange,
    filteredItems: filteredMilestones,
  } = useAdminListSearch<IMilestone>({
    items: milestones,
    searchFields: ["name", "description"] as (keyof IMilestone)[],
  });

  const {
    sortColumn: milestoneSortColumn,
    isSortDescending: isMilestoneSortDescending,
    sortItems: sortMilestones,
    onSortColumnChanged: onMilestoneSortColumnChanged,
    onClearColumnChanged: onMilestoneSortClearChanged,
  } = useAdminListSort<IMilestone>({
    cacheKey: "Milestones",
    defaultSortColumn: "dueDate",
    defaultDescending: false,
  });
  const milestoneColumns = useMemo<ITableColumn<IMilestone>[]>(
    () => [
      {
        columnId: "name",
        displayValue: "Name",
        className: colStyles.wide,
        renderHeaderCell: () => "Name",
        compare: (a, b) => a.name.localeCompare(b.name),
        renderCell: (item) => item.name,
      },
      {
        columnId: "description",
        displayValue: "Description",
        className: colStyles.wide,
        renderHeaderCell: () => "Description",
        compare: (a, b) =>
          (a.description ?? "").localeCompare(b.description ?? ""),
        renderCell: (item) => item.description || "—",
      },
      {
        columnId: "dueDate",
        displayValue: "Due Date",
        className: colStyles.normal,
        renderHeaderCell: () => "Due Date",
        compare: (a, b) => (a.dueDate ?? "").localeCompare(b.dueDate ?? ""),
        renderCell: (item) =>
          formatDate(
            item.dueDate
              ? Math.floor(
                  new Date(item.dueDate + "T00:00:00").getTime() / 1000,
                )
              : undefined,
            "—",
          ),
      },
      {
        columnId: "status",
        displayValue: "Status",
        className: colStyles.normal,
        renderHeaderCell: () => "Status",
        compare: (a, b) => a.status.localeCompare(b.status),
        renderCell: (item) => (
          <span className={statusTriggerClass(item.status)}>
            {
              MILESTONE_STATUS_OPTIONS.find((o) => o.value === item.status)
                ?.label
            }
          </span>
        ),
      },
    ],
    [colStyles],
  );
  const sortedMilestones = useMemo(
    () => sortMilestones(filteredMilestones, milestoneColumns),
    [filteredMilestones, sortMilestones, milestoneColumns],
  );
  const {
    paginatedItems: paginatedMilestones,
    selectedPageIndex: milestonePageIndex,
    pageCount: milestonePageCount,
    handlePageChange: handleMilestonePageChange,
    totalItemsCount: milestoneTotalCount,
    itemsPerPage: milestoneItemsPerPage,
  } = useAdminListPagination<IMilestone>({
    items: sortedMilestones,
    itemsPerPage: 15,
  });
  const hasSowDraftedMilestones = useMemo(
    () => milestones.some((m) => m.isDraftedFromSow),
    [milestones],
  );
  const milestoneActionsWidth = hasSowDraftedMilestones
    ? ACTIONS_COLUMN_WIDTH_WIDE
    : ACTIONS_COLUMN_WIDTH_NARROW;
  const milestoneRowActions = useMemo<IAdminRowAction<IMilestone>[]>(
    () => [
      ...(hasSowDraftedMilestones
        ? [
            {
              icon: <SparkleRegular />,
              tooltip: "Drafted from SOW",
              isNonInteractive: true,
              isVisible: (item: IMilestone) => !!item.isDraftedFromSow,
            },
            {
              icon: <CheckmarkCircleRegular />,
              tooltip: "Confirm and keep",
              onClick: (item: IMilestone) => handleConfirmKeepMilestone(item),
              isVisible: (item: IMilestone) => !!item.isDraftedFromSow,
            },
          ]
        : []),
      {
        icon: <EditRegular />,
        tooltip: "Edit",
        onClick: (item) => handleOpenMilestoneDialog(item),
      },
      {
        icon: <DeleteRegular />,
        tooltip: "Delete",
        onClick: (item) => handleDeleteMilestone(item.id),
      },
    ],
    [hasSowDraftedMilestones, handleConfirmKeepMilestone],
  );

  const {
    searchQuery: raidSearchQuery,
    handleSearchChange: handleRaidSearchChange,
    filteredItems: filteredRaidEntries,
  } = useAdminListSearch<IRaidEntry>({
    items: raidEntries,
    searchFields: ["name", "description", "owner"] as (keyof IRaidEntry)[],
  });

  const {
    sortColumn: raidSortColumn,
    isSortDescending: isRaidSortDescending,
    sortItems: sortRaidItems,
    onSortColumnChanged: onRaidSortColumnChanged,
    onClearColumnChanged: onRaidSortClearChanged,
  } = useAdminListSort<IRaidEntry>({
    cacheKey: "RaidEntries",
    defaultSortColumn: "name",
    defaultDescending: false,
  });
  const raidColumns = useMemo<ITableColumn<IRaidEntry>[]>(
    () => [
      {
        columnId: "name",
        displayValue: "Name",
        className: colStyles.wide,
        renderHeaderCell: () => "Name",
        compare: (a, b) => (a.name ?? "").localeCompare(b.name ?? ""),
        renderCell: (item) => item.name,
      },
      {
        columnId: "category",
        displayValue: "Type",
        className: colStyles.normal,
        renderHeaderCell: () => "Type",
        compare: (a, b) => a.category.localeCompare(b.category),
        renderCell: (item) => (
          <span
            className={mergeClasses(styles.raidPill, styles.raidCategoryPill)}
          >
            {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
          </span>
        ),
      },
      {
        columnId: "severity",
        displayValue: "Severity",
        className: colStyles.normal,
        renderHeaderCell: () => "Severity",
        compare: (a, b) => a.severity.localeCompare(b.severity),
        renderCell: (item) => (
          <span
            className={mergeClasses(
              styles.raidPill,
              raidSeverityClass(item.severity),
            )}
          >
            {item.severity.charAt(0).toUpperCase() + item.severity.slice(1)}
          </span>
        ),
      },
      {
        columnId: "dueDate",
        displayValue: "Due Date",
        className: colStyles.normal,
        renderHeaderCell: () => "Due Date",
        compare: (a, b) => (a.dueDate ?? "").localeCompare(b.dueDate ?? ""),
        renderCell: (item) =>
          formatDate(
            item.dueDate
              ? Math.floor(
                  new Date(item.dueDate + "T00:00:00").getTime() / 1000,
                )
              : undefined,
            "—",
          ),
      },
      {
        columnId: "owner",
        displayValue: "Owner",
        className: colStyles.normal,
        renderHeaderCell: () => "Owner",
        compare: (a, b) => (a.owner ?? "").localeCompare(b.owner ?? ""),
        renderCell: (item) => item.owner || "—",
      },
      {
        columnId: "status",
        displayValue: "Status",
        className: colStyles.normal,
        renderHeaderCell: () => "Status",
        compare: (a, b) => a.status.localeCompare(b.status),
        renderCell: (item) =>
          RAID_STATUS_OPTIONS.find((o) => o.value === item.status)?.label ??
          item.status,
      },
    ],
    [colStyles, styles],
  );
  const sortedRaidEntries = useMemo(
    () => sortRaidItems(filteredRaidEntries, raidColumns),
    [filteredRaidEntries, sortRaidItems, raidColumns],
  );
  const {
    paginatedItems: paginatedRaidEntries,
    selectedPageIndex: raidPageIndex,
    pageCount: raidPageCount,
    handlePageChange: handleRaidPageChange,
    totalItemsCount: raidTotalCount,
    itemsPerPage: raidItemsPerPage,
  } = useAdminListPagination<IRaidEntry>({
    items: sortedRaidEntries,
    itemsPerPage: 15,
  });
  const hasSowDraftedRaidEntries = useMemo(
    () => raidEntries.some((e) => e.isDraftedFromSow),
    [raidEntries],
  );
  const raidActionsWidth = hasSowDraftedRaidEntries
    ? ACTIONS_COLUMN_WIDTH_WIDE
    : ACTIONS_COLUMN_WIDTH_NARROW;
  const raidRowActions = useMemo<IAdminRowAction<IRaidEntry>[]>(
    () => [
      ...(hasSowDraftedRaidEntries
        ? [
            {
              icon: <SparkleRegular />,
              tooltip: "Drafted from SOW",
              isNonInteractive: true,
              isVisible: (item: IRaidEntry) => !!item.isDraftedFromSow,
            },
            {
              icon: <CheckmarkCircleRegular />,
              tooltip: "Confirm and keep",
              onClick: (item: IRaidEntry) => handleConfirmKeepRaidEntry(item),
              isVisible: (item: IRaidEntry) => !!item.isDraftedFromSow,
            },
          ]
        : []),
      {
        icon: <EditRegular />,
        tooltip: "Edit",
        onClick: (item) => handleOpenRaidDialog(item),
      },
      {
        icon: <DeleteRegular />,
        tooltip: "Delete",
        onClick: (item) => handleDeleteRaidEntry(item.id),
      },
    ],
    [hasSowDraftedRaidEntries, handleConfirmKeepRaidEntry],
  );

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        <div className={styles.content}>
          {sowAnalysisStatus && !isSowBannerDismissed && (
            <div
              role={sowAnalysisStatus.status === "failed" ? "alert" : "status"}
              aria-live={
                sowAnalysisStatus.status === "failed" ? "assertive" : "polite"
              }
              aria-atomic="true"
            >
              {sowAnalysisStatus.status === "processing" &&
                !isSowAnalysisPollTimedOut && (
                  <MessageBar
                    intent="info"
                    className={styles.sowAnalysisBanner}
                  >
                    <div className={styles.sowAnalysisBannerContent}>
                      <Spinner size="tiny" label="Analyzing SOW" />
                      <span>Analyzing SOW to populate your plan...</span>
                    </div>
                  </MessageBar>
                )}
              {sowAnalysisStatus.status === "processing" &&
                isSowAnalysisPollTimedOut && (
                  <MessageBar
                    intent="warning"
                    className={styles.sowAnalysisBanner}
                  >
                    <div className={styles.sowAnalysisBannerRow}>
                      <span>
                        SOW analysis is taking longer than expected. Check back
                        later, or use "Analyze SOW" on the document to try
                        again.
                      </span>
                      <Button
                        appearance="subtle"
                        icon={<DismissRegular />}
                        aria-label="Dismiss"
                        onClick={() => setIsSowBannerDismissed(true)}
                      />
                    </div>
                  </MessageBar>
                )}
              {sowAnalysisStatus.status === "completed" && (
                <MessageBar
                  intent="success"
                  className={styles.sowAnalysisBanner}
                >
                  <div className={styles.sowAnalysisBannerRow}>
                    <span>
                      SOW analysis completed. New items have been added to your
                      plan.
                    </span>
                    <Button
                      appearance="subtle"
                      icon={<DismissRegular />}
                      aria-label="Dismiss"
                      onClick={() => setIsSowBannerDismissed(true)}
                    />
                  </div>
                </MessageBar>
              )}
              {sowAnalysisStatus.status === "failed" && (
                <MessageBar intent="error" className={styles.sowAnalysisBanner}>
                  <div className={styles.sowAnalysisBannerRow}>
                    <span>
                      SOW analysis failed:{" "}
                      {sowAnalysisStatus.errorMessage || "Unknown error"}
                    </span>
                    <Button
                      appearance="subtle"
                      icon={<DismissRegular />}
                      aria-label="Dismiss"
                      onClick={() => setIsSowBannerDismissed(true)}
                    />
                  </div>
                </MessageBar>
              )}
              {sowAnalysisStatus.status === "skipped_not_empty" && (
                <MessageBar
                  intent="warning"
                  className={styles.sowAnalysisBanner}
                >
                  <div className={styles.sowAnalysisBannerRow}>
                    <span>
                      SOW analysis was skipped because your plan already has
                      content.
                    </span>
                    <Button
                      appearance="subtle"
                      icon={<DismissRegular />}
                      aria-label="Dismiss"
                      onClick={() => setIsSowBannerDismissed(true)}
                    />
                  </div>
                </MessageBar>
              )}
              {sowAnalysisStatus.status === "skipped_no_content" && (
                <MessageBar
                  intent="warning"
                  className={styles.sowAnalysisBanner}
                >
                  <div className={styles.sowAnalysisBannerRow}>
                    <span>
                      No extractable content found in the SOW document.
                    </span>
                    <Button
                      appearance="subtle"
                      icon={<DismissRegular />}
                      aria-label="Dismiss"
                      onClick={() => setIsSowBannerDismissed(true)}
                    />
                  </div>
                </MessageBar>
              )}
            </div>
          )}
          <div className={styles.ragSectionHeader}>
            <span className={styles.ragSectionLabel}>Project status</span>
            <span className={styles.ragSectionHint}>
              Click a dot to change the rating
            </span>
          </div>
          <div className={styles.ragCard}>
            {ragDimensions.map((dimension, index) => {
              const startDateError =
                ragDateErrors[ragDateErrorKey(dimension.id, "startDate")];
              const endDateError =
                ragDateErrors[ragDateErrorKey(dimension.id, "endDate")];

              return (
                <div
                  key={dimension.id}
                  className={mergeClasses(
                    styles.ragColumn,
                    index > 0 && styles.ragColumnBorder,
                  )}
                >
                  <span className={styles.ragColumnLabel}>
                    {dimension.label}
                  </span>
                  {dimension.id === "schedule" ? (
                    <>
                      <div className={styles.ragColumnStatus}>
                        <button
                          className={mergeClasses(
                            styles.ragDot,
                            ragDotClass(dimension.status),
                          )}
                          onClick={() => handleRagCycle(dimension.id)}
                          title="Cycle rating"
                          aria-label={`Schedule status. Click to cycle.`}
                        />
                        <span className={styles.ragWeekLabel}>
                          {computeWeekLabel(
                            dimension.startDate,
                            dimension.endDate,
                          )}
                        </span>
                      </div>
                      <div className={styles.scheduleDates}>
                        <input
                          type="date"
                          min={MIN_PLAN_DATE}
                          max={MAX_PLAN_DATE}
                          className={mergeClasses(
                            styles.scheduleDateInput,
                            startDateError && styles.scheduleDateInputError,
                          )}
                          value={dimension.startDate ?? ""}
                          onChange={(e) =>
                            handleRagDateChange(
                              dimension.id,
                              "startDate",
                              e.target.value,
                            )
                          }
                          aria-invalid={Boolean(startDateError)}
                          aria-label="Schedule start date"
                        />
                        <span className={styles.scheduleDateSep}>→</span>
                        <input
                          type="date"
                          min={MIN_PLAN_DATE}
                          max={MAX_PLAN_DATE}
                          className={mergeClasses(
                            styles.scheduleDateInput,
                            endDateError && styles.scheduleDateInputError,
                          )}
                          value={dimension.endDate ?? ""}
                          onChange={(e) =>
                            handleRagDateChange(
                              dimension.id,
                              "endDate",
                              e.target.value,
                            )
                          }
                          aria-invalid={Boolean(endDateError)}
                          aria-label="Schedule end date"
                        />
                      </div>
                      {(startDateError || endDateError) && (
                        <span className={styles.scheduleDateError} role="alert">
                          {INVALID_DATE_MESSAGE}
                        </span>
                      )}
                    </>
                  ) : (
                    <div className={styles.ragColumnStatus}>
                      <button
                        className={mergeClasses(
                          styles.ragDot,
                          ragDotClass(dimension.status),
                        )}
                        onClick={() => handleRagCycle(dimension.id)}
                        title="Cycle rating"
                        aria-label={`${dimension.label}: ${dimension.statusText}. Click to cycle.`}
                      />
                      {editingRagStatusId === dimension.id ? (
                        <input
                          className={styles.ragStatusInput}
                          autoFocus
                          value={dimension.statusText}
                          onChange={(e) =>
                            handleRagStatusTextChange(
                              dimension.id,
                              e.target.value,
                            )
                          }
                          onBlur={() => setEditingRagStatusId(null)}
                          aria-label={`Status text for ${dimension.label}`}
                        />
                      ) : (
                        <span
                          className={styles.ragStatusInput}
                          onClick={() => setEditingRagStatusId(dimension.id)}
                          title="Click to edit"
                          role="button"
                          aria-label={`Status text for ${dimension.label}: ${dimension.statusText}. Click to edit.`}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              setEditingRagStatusId(dimension.id);
                          }}
                        >
                          {dimension.statusText}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className={styles.planTabBar}>
            <TabList
              selectedValue={activePlanTab}
              onTabSelect={(_ev: SelectTabEvent, data: SelectTabData) =>
                setActivePlanTab(data.value as PlanTab)
              }
              aria-label="Plan sections"
            >
              <Tab id="tab-milestones" value="milestones">
                Milestones
              </Tab>
              <Tab id="tab-deliverables" value="deliverables">
                Deliverables
              </Tab>
              <Tab id="tab-raid" value="raid">
                RAID Log
              </Tab>
            </TabList>
          </div>

          <div
            className={styles.bannerSlot}
            aria-live="polite"
            aria-atomic="true"
          >
            {activePlanTab === "deliverables" &&
              deliverableLinks.length > 0 && (
                <div className={styles.raidNoteBanner}>
                  <span>Quality guidance:</span>{" "}
                  {deliverableLinks.map((link, i) => (
                    <Fragment key={link.id}>
                      {i > 0 && " · "}
                      <a href={link.url} target="_blank" rel="noreferrer">
                        {link.displayName}
                      </a>
                    </Fragment>
                  ))}
                </div>
              )}
          </div>

          {activePlanTab === "milestones" && (
            <div role="tabpanel" aria-labelledby="tab-milestones" tabIndex={0}>
              <AdminListPage<IMilestone>
                items={milestones}
                columns={milestoneColumns}
                noPadding
                isLoading={saveStatus === "saving"}
                searchPlaceholder="Search milestones..."
                searchQuery={milestoneSearchQuery}
                onSearchChange={handleMilestoneSearchChange}
                rowActions={milestoneRowActions}
                headerActions={[
                  {
                    label: "Add New",
                    icon: <AddRegular aria-hidden="true" />,
                    onClick: () => handleOpenMilestoneDialog(),
                    appearance: "primary",
                  },
                ]}
                getRowId={(item) => item.id}
                sortColumn={milestoneSortColumn}
                isSortDescending={isMilestoneSortDescending}
                onSortChange={onMilestoneSortColumnChanged}
                onSortClear={onMilestoneSortClearChanged}
                paginatedItems={paginatedMilestones}
                selectedPageIndex={milestonePageIndex}
                pageCount={milestonePageCount}
                totalItemsCount={milestoneTotalCount}
                itemsPerPage={milestoneItemsPerPage}
                onPageChange={handleMilestonePageChange}
                actionsWidth={milestoneActionsWidth}
                emptyStateMessage={
                  milestoneSearchQuery
                    ? "No milestones match the search."
                    : "No milestones yet."
                }
              />
            </div>
          )}

          {activePlanTab === "deliverables" && (
            <div
              role="tabpanel"
              aria-labelledby="tab-deliverables"
              tabIndex={0}
            >
              <DeliverablesPage workspaceId={workspaceId} />
            </div>
          )}

          {activePlanTab === "raid" && (
            <div role="tabpanel" aria-labelledby="tab-raid" tabIndex={0}>
              <AdminListPage<IRaidEntry>
                items={raidEntries}
                columns={raidColumns}
                noPadding
                isLoading={saveStatus === "saving"}
                searchPlaceholder="Search RAID entries..."
                searchQuery={raidSearchQuery}
                onSearchChange={handleRaidSearchChange}
                rowActions={raidRowActions}
                headerActions={[
                  {
                    label: "Add New",
                    icon: <AddRegular aria-hidden="true" />,
                    onClick: () => handleOpenRaidDialog(),
                    appearance: "primary",
                  },
                ]}
                getRowId={(item) => item.id}
                sortColumn={raidSortColumn}
                isSortDescending={isRaidSortDescending}
                onSortChange={onRaidSortColumnChanged}
                onSortClear={onRaidSortClearChanged}
                paginatedItems={paginatedRaidEntries}
                selectedPageIndex={raidPageIndex}
                pageCount={raidPageCount}
                totalItemsCount={raidTotalCount}
                itemsPerPage={raidItemsPerPage}
                onPageChange={handleRaidPageChange}
                actionsWidth={raidActionsWidth}
                emptyStateMessage={
                  raidSearchQuery
                    ? "No RAID entries match the search."
                    : "No RAID entries yet."
                }
              />
            </div>
          )}

          <div className={styles.infoBanner}>
            <SparkleRegular
              className={styles.infoBannerIcon}
              aria-hidden="true"
            />
            <Text className={styles.infoBannerText}>
              Milestones, deliverables, and RAID entries marked with the SOW
              icon were auto-drafted from your uploaded Statement of Work.
              Manually created or edited entries are never overwritten by AI
              extraction.
            </Text>
          </div>
        </div>
      </div>

      <Dialog
        open={isRaidDialogOpen}
        onOpenChange={(_, data) => {
          if (!data.open) handleDialogClose();
        }}
      >
        <DialogSurface
          className={mergeClasses(
            contentStyles.dialogSurface,
            styles.dialogSurface,
          )}
        >
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  aria-label="Close"
                  onClick={handleDialogClose}
                />
              }
            >
              {dialogDraft && raidEntries.some((e) => e.id === dialogDraft.id)
                ? "Edit RAID entry"
                : "Add RAID entry"}
            </DialogTitle>
            <DialogContent
              className={mergeClasses(
                contentStyles.tabbedDialogContent,
                styles.dialogContent,
              )}
            >
              <DialogTabs
                tabs={[
                  { key: "overview", label: "Overview" },
                  { key: "action", label: "Action" },
                ]}
                activeTab={activeRaidTab}
                onTabClick={(tab) => {
                  if (tab === "action") {
                    const nameError = validateRaidName(dialogDraft?.name ?? "");
                    if (nameError) {
                      setDialogNameError(nameError);
                      return;
                    }
                  }
                  setActiveRaidTab(tab as "overview" | "action");
                }}
              />
              <div className={contentStyles.formContainer}>
                {activeRaidTab === "overview" && (
                  <div className={contentStyles.tabContent} role="tabpanel">
                    <Field
                      label="Name"
                      required
                      validationState={dialogNameError ? "error" : "none"}
                      validationMessage={dialogNameError || undefined}
                    >
                      <Input
                        value={dialogDraft?.name ?? ""}
                        onChange={(_e, d) => {
                          handleDraftChange("name", d.value);
                          if (dialogNameError) setDialogNameError("");
                        }}
                        placeholder="Short title"
                      />
                    </Field>
                    <Field label="Description">
                      <Textarea
                        value={dialogDraft?.description ?? ""}
                        onChange={(_e, d) =>
                          handleDraftChange("description", d.value)
                        }
                        resize="vertical"
                        rows={4}
                        placeholder="Describe the risk, action, issue, or decision"
                      />
                    </Field>
                    <Field label="Type">
                      <Dropdown
                        value={
                          RAID_CATEGORY_OPTIONS.find(
                            (o) => o.value === dialogDraft?.category,
                          )?.label ?? "Risk"
                        }
                        selectedOptions={[dialogDraft?.category ?? "risk"]}
                        onOptionSelect={(_e, d) =>
                          handleDraftChange("category", d.optionValue ?? "risk")
                        }
                      >
                        {RAID_CATEGORY_OPTIONS.map((o) => (
                          <Option key={o.value} value={o.value}>
                            {o.label}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                    <Field label="Importance / Severity">
                      <Dropdown
                        value={
                          RAID_SEVERITY_OPTIONS.find(
                            (o) => o.value === dialogDraft?.severity,
                          )?.label ?? "Medium"
                        }
                        selectedOptions={[dialogDraft?.severity ?? "medium"]}
                        onOptionSelect={(_e, d) =>
                          handleDraftChange(
                            "severity",
                            d.optionValue ?? "medium",
                          )
                        }
                      >
                        {RAID_SEVERITY_OPTIONS.map((o) => (
                          <Option key={o.value} value={o.value}>
                            {o.label}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                  </div>
                )}
                {activeRaidTab === "action" && (
                  <div className={contentStyles.tabContent} role="tabpanel">
                    <Field label="Response / Mitigation">
                      <Textarea
                        value={dialogDraft?.responseMitigation ?? ""}
                        onChange={(_e, d) =>
                          handleDraftChange("responseMitigation", d.value)
                        }
                        resize="vertical"
                        rows={4}
                        placeholder="Planned response or mitigation steps"
                      />
                    </Field>
                    <Field
                      label="Due Date"
                      validationState={dialogDueDateError ? "error" : "none"}
                      validationMessage={dialogDueDateError || undefined}
                    >
                      <Input
                        type="date"
                        min={MIN_PLAN_DATE}
                        max={MAX_PLAN_DATE}
                        value={dialogDraft?.dueDate ?? ""}
                        onChange={(_e, d) => {
                          handleDraftChange("dueDate", d.value);
                          setDialogDueDateError(validatePlanDate(d.value));
                        }}
                      />
                    </Field>
                    <Field label="Owner">
                      <PeoplePicker
                        id="raid-owner-picker"
                        selectedOptions={dialogOwnerOptions}
                        handleSelectedItems={setDialogOwnerOptions}
                        readResourcesForPickerFieldFiltered={
                          readResourcesForPickerFieldFiltered
                        }
                        hasSingleSelection
                        hasOnlyOurUsers
                      />
                    </Field>
                    <Field label="Status">
                      <Dropdown
                        value={
                          RAID_STATUS_OPTIONS.find(
                            (o) => o.value === dialogDraft?.status,
                          )?.label ?? "Not Started"
                        }
                        selectedOptions={[dialogDraft?.status ?? "not_started"]}
                        onOptionSelect={(_e, d) =>
                          handleDraftChange(
                            "status",
                            d.optionValue ?? "not_started",
                          )
                        }
                      >
                        {RAID_STATUS_OPTIONS.map((o) => (
                          <Option key={o.value} value={o.value}>
                            {o.label}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                  </div>
                )}
              </div>
            </DialogContent>
          </DialogBody>
          <div className={footerStyles.dialogFooter}>
            {activeRaidTab === "action" && (
              <DialogFooterAction
                icon={<ArrowLeftRegular />}
                tooltip="Back"
                onClick={() => setActiveRaidTab("overview")}
              />
            )}
            {activeRaidTab === "overview" && (
              <DialogFooterAction
                icon={<ArrowRightRegular />}
                tooltip="Next"
                onClick={() => {
                  const nameError = validateRaidName(dialogDraft?.name ?? "");
                  if (nameError) {
                    setDialogNameError(nameError);
                    return;
                  }
                  setActiveRaidTab("action");
                }}
              />
            )}
            {activeRaidTab === "action" && (
              <DialogFooterAction
                icon={<CheckmarkRegular />}
                tooltip="Save entry"
                ariaLabel={
                  !dialogDraft?.name?.trim()
                    ? "Save entry (name is required)"
                    : "Save entry"
                }
                dimmed={!dialogDraft?.name?.trim()}
                onClick={handleSaveRaidEntry}
              />
            )}
          </div>
        </DialogSurface>
      </Dialog>

      <ActionDialog
        isVisible={confirmDeleteId !== null}
        title="Delete RAID entry"
        message={`Delete "${raidEntries.find((e) => e.id === confirmDeleteId)?.name ?? "this entry"}"? This cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={handleCancelDelete}
      />

      <ActionDialog
        isVisible={confirmDeleteMilestoneId !== null}
        title="Delete milestone"
        message={`Delete "${milestones.find((m) => m.id === confirmDeleteMilestoneId)?.name ?? "this milestone"}"? This cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDeleteMilestone}
        onDismiss={handleCancelDeleteMilestone}
      />

      <Dialog
        open={isMilestoneDialogOpen}
        onOpenChange={(_, data) => {
          if (!data.open) handleCloseMilestoneDialog();
        }}
      >
        <DialogSurface
          className={mergeClasses(
            contentStyles.dialogSurface,
            styles.dialogSurface,
          )}
        >
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  aria-label="Close"
                  onClick={handleCloseMilestoneDialog}
                />
              }
            >
              {milestoneDraft &&
              milestones.some((m) => m.id === milestoneDraft.id)
                ? "Edit milestone"
                : "Add milestone"}
            </DialogTitle>
            <DialogContent
              className={mergeClasses(
                contentStyles.tabbedDialogContent,
                styles.dialogContent,
              )}
            >
              <div className={contentStyles.formContainer}>
                <Field
                  label="Name"
                  required
                  validationState={milestoneNameError ? "error" : "none"}
                  validationMessage={milestoneNameError || undefined}
                >
                  <Input
                    value={milestoneDraft?.name ?? ""}
                    onChange={(_e, d) => {
                      handleDraftMilestoneChange("name", d.value);
                      if (milestoneNameError) setMilestoneNameError("");
                    }}
                    placeholder="Milestone name"
                  />
                </Field>
                <Field label="Description">
                  <Textarea
                    value={milestoneDraft?.description ?? ""}
                    onChange={(_e, d) =>
                      handleDraftMilestoneChange("description", d.value)
                    }
                    placeholder="Optional description"
                    rows={2}
                  />
                </Field>
                <Field
                  label="Due date"
                  validationState={milestoneDueDateError ? "error" : "none"}
                  validationMessage={milestoneDueDateError || undefined}
                >
                  <Input
                    type="date"
                    min={MIN_PLAN_DATE}
                    max={MAX_PLAN_DATE}
                    value={milestoneDraft?.dueDate ?? ""}
                    onChange={(_e, d) => {
                      handleDraftMilestoneChange("dueDate", d.value);
                      setMilestoneDueDateError(validatePlanDate(d.value));
                    }}
                  />
                </Field>
                <Field label="Status">
                  <Dropdown
                    value={
                      MILESTONE_STATUS_OPTIONS.find(
                        (o) => o.value === milestoneDraft?.status,
                      )?.label ?? "Not Started"
                    }
                    selectedOptions={[milestoneDraft?.status ?? "not_started"]}
                    onOptionSelect={(_e, d) =>
                      handleDraftMilestoneChange(
                        "status",
                        d.optionValue ?? "not_started",
                      )
                    }
                  >
                    {MILESTONE_STATUS_OPTIONS.map((o) => (
                      <Option key={o.value} value={o.value}>
                        {o.label}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
              </div>
            </DialogContent>
          </DialogBody>
          <div className={footerStyles.dialogFooter}>
            <DialogFooterAction
              icon={<CheckmarkRegular />}
              tooltip="Save milestone"
              ariaLabel={
                !milestoneDraft?.name?.trim()
                  ? "Save milestone (name is required)"
                  : "Save milestone"
              }
              dimmed={!milestoneDraft?.name?.trim()}
              onClick={handleSaveMilestone}
            />
          </div>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
